import { motion } from 'framer-motion'
import { useEffect, useId, useRef, useState, type RefObject } from 'react'

/** Ré-échantillonnage pendant que l'animation d'entrée se stabilise (~2 s), puis arrêt. */
const SETTLE_INTERVAL_MS = 250
const SETTLE_TICKS = 8
/** Après un resize, la géométrie est déjà quasi stable : deux mesures suffisent. */
const RESETTLE_TICKS = 2

type AnimatedBeamProps = {
  containerRef: RefObject<HTMLElement>
  fromRef: RefObject<HTMLElement>
  toRef: RefObject<HTMLElement>
  curvature?: number
  reverse?: boolean
  pathColor?: string
  pathWidth?: number
  pathOpacity?: number
  gradientStartColor?: string
  gradientStopColor?: string
  delay?: number
  duration?: number
  startXOffset?: number
  startYOffset?: number
  endXOffset?: number
  endYOffset?: number
}

export function AnimatedBeam({
  containerRef,
  fromRef,
  toRef,
  curvature = 0,
  reverse = false,
  pathColor = '#d9d9de',
  pathWidth = 2,
  pathOpacity = 0.2,
  gradientStartColor = '#5e84ff',
  gradientStopColor = '#3b5dff',
  delay = 0,
  duration = 3,
  startXOffset = 0,
  startYOffset = 0,
  endXOffset = 0,
  endYOffset = 0,
}: AnimatedBeamProps) {
  const id = useId()
  const [pathD, setPathD] = useState('')
  const [svgDims, setSvgDims] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const measure = () => {
      const c = containerRef.current
      const f = fromRef.current
      const t = toRef.current
      if (!c || !f || !t) return
      const cr = c.getBoundingClientRect()
      const fr = f.getBoundingClientRect()
      const tr = t.getBoundingClientRect()

      // Renvoyer l'objet précédent quand rien ne bouge : sinon une nouvelle
      // référence à chaque mesure re-rendrait le SVG pour rien.
      setSvgDims((prev) =>
        prev.width === cr.width && prev.height === cr.height
          ? prev
          : { width: cr.width, height: cr.height }
      )

      const startX = fr.left - cr.left + fr.width / 2 + startXOffset
      const startY = fr.top - cr.top + fr.height / 2 + startYOffset
      const endX = tr.left - cr.left + tr.width / 2 + endXOffset
      const endY = tr.top - cr.top + tr.height / 2 + endYOffset

      const controlY = startY - curvature
      setPathD(`M ${startX},${startY} Q ${(startX + endX) / 2},${controlY} ${endX},${endY}`)
    }

    // Les pastilles entrent en scène via des transforms framer-motion, que
    // ResizeObserver ne détecte pas. On ré-échantillonne donc le temps que
    // l'animation d'entrée se stabilise, puis on s'arrête — au lieu de
    // sonder la géométrie indéfiniment (chaque mesure force un reflow).
    let settleTimer = 0
    const settle = (ticks = SETTLE_TICKS) => {
      window.clearInterval(settleTimer)
      let left = ticks
      settleTimer = window.setInterval(() => {
        measure()
        if (--left <= 0) window.clearInterval(settleTimer)
      }, SETTLE_INTERVAL_MS)
    }

    const ro = new ResizeObserver(() => {
      measure()
      settle(RESETTLE_TICKS)
    })
    ro.observe(container)
    if (fromRef.current) ro.observe(fromRef.current)
    if (toRef.current) ro.observe(toRef.current)

    // Hors écran, il n'y a rien à mesurer : sur mobile la section passe
    // l'essentiel de son temps en dehors du viewport.
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        measure()
        settle()
      } else {
        window.clearInterval(settleTimer)
      }
    })
    io.observe(container)

    const onResize = () => {
      measure()
      settle(RESETTLE_TICKS)
    }
    window.addEventListener('resize', onResize)

    measure()

    return () => {
      ro.disconnect()
      io.disconnect()
      window.removeEventListener('resize', onResize)
      window.clearInterval(settleTimer)
    }
  }, [containerRef, fromRef, toRef, curvature, startXOffset, startYOffset, endXOffset, endYOffset])

  return (
    <svg
      fill="none"
      width={svgDims.width}
      height={svgDims.height}
      xmlns="http://www.w3.org/2000/svg"
      className="pointer-events-none absolute left-0 top-0 transform-gpu stroke-2"
      viewBox={`0 0 ${svgDims.width} ${svgDims.height}`}
    >
      <path d={pathD} stroke={pathColor} strokeWidth={pathWidth} strokeOpacity={pathOpacity} strokeLinecap="round" />
      <path d={pathD} strokeWidth={pathWidth} stroke={`url(#${id})`} strokeOpacity="1" strokeLinecap="round" />
      <defs>
        <motion.linearGradient
          className="transform-gpu"
          id={id}
          gradientUnits="userSpaceOnUse"
          initial={{ x1: '0%', x2: '0%', y1: '0%', y2: '0%' }}
          animate={{
            x1: reverse ? ['90%', '-10%'] : ['-10%', '110%'],
            x2: reverse ? ['100%', '0%'] : ['0%', '120%'],
            y1: ['0%', '0%'],
            y2: ['0%', '0%'],
          }}
          transition={{
            delay,
            duration,
            ease: [0.16, 1, 0.3, 1],
            repeat: Infinity,
            repeatDelay: 0.4,
          }}
        >
          <stop stopColor={gradientStartColor} stopOpacity="0" />
          <stop stopColor={gradientStartColor} />
          <stop offset="32.5%" stopColor={gradientStopColor} />
          <stop offset="100%" stopColor={gradientStopColor} stopOpacity="0" />
        </motion.linearGradient>
      </defs>
    </svg>
  )
}
